import {each, isEmpty, orderBy} from 'lodash';
import {FilterTypes} from '@ey/canvascoreservices';
import {labels, currentResource} from '../../../util/utils';
import {
	userStatus,
	documentTypes,
	filterStatus,
	GARoleTypeId,
	TaskCategory,
	TaskSubCategory,
	TaskTypeEnum,
	ViewTypes
} from '../../../util/uiconstants';

export const DocumentFilterEntityTypes = {
	engagementUser: 'engagementUser',
	reviewNoteStatus: 'reviewNoteStatus',
	fileExtension: 'fileExtension',
	documentContent: 'documentContent',
	sharedEvidence: 'sharedEvidence',
	groupAuditComponent: 'groupAuditComponent',
	taskId: 'taskId',
	documentFindings: 'documentFindings',
	canvasForms: 'canvasForms',
	timePhases: 'timePhases'
};

export const DocumentFilterEntityConfiguration = {
	engagementUser: {
		endpoint: 'users',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			userStatus: [userStatus.active, userStatus.deActivated]
		}),
		entityMapping: (response) => {
			let users = response.users;

			users = orderBy(users, (user) => user.engagementUserFirstName + ' ' + user.engagementUserLastName);

			return users.map((item) => ({
				value: item.userId,
				label: item.engagementUserFirstName + ' ' + item.engagementUserLastName
			}));
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			}

			return value;
		}
	},
	reviewNoteStatus: {
		data: 'filterReviewNoteStatus'
	},
	fileExtension: {
		data: 'filterFileExtension'
	},
	documentContent: {
		data: 'filterDocumentContent'
	},
	sharedEvidence: {
		data: 'filterSharedEvidence'
	},
	groupAuditComponent: {
		endpoint: 'groupcomponents',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId
		}),
		entityMapping: (response) =>
			response.data?.map((item) => ({
				value: item.id,
				label: item.shortName
			}))
	},
	taskId: {
		endpoint: 'tasks',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			categoryId: TaskCategory.execution,
			subCategoryId: TaskSubCategory.all,
			taskType: TaskTypeEnum.PRIMARY_GROUP_TASK
		}),
		entityMapping: (response) =>
			response.taskGroups[0]?.tasks.map((item) => ({
				value: item.id,
				label: item.taskDisplayName
			}))
	},
	documentFindings: {
		data: 'filterFindings'
	},
	canvasForms: {
		data: 'filterCanvasForms'
	},
	signoffRequirements: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.filterSignoffRequirements.find((x) => x.value == id)?.label
	},
	missingRelatedTask: {
		data: 'filterMissingRelatedTask',
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.filterMissingRelatedTask.find((x) => x.value == id)?.label
	},
	millestone: {
		endpoint: 'timephases',
		entityFilter: (entityFilter) => ({
			engagementId: entityFilter.engagementId
		}),
		entityMapping: (response) =>
			response.data?.map((item) => ({
				value: item.id,
				label: item.shortName
			}))
	},
	timePhase: {
		endpoint: 'timephases',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId
		}),
		entityMapping: (response) => {
			let timephases = [];
			response?.forEach((timephase) => {
				timephase.timePhases.length > 0 &&
					timephase.timePhases.forEach((timephaseitem) => {
						timephases.push({
							value: timephaseitem.id.toString(),
							label: timephaseitem.periodName
						});
					});
			});

			return timephases;
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId && _filterData.byId[id]) {
				value = _filterData.byId[id].label;
			}

			return value;
		}
	}
};

export const TaskFilterEntityTypes = {
	engagementUser: 'engagementUser',
	engagementUserRole: 'engagementUserRole',
	reviewNoteStatus: 'reviewNoteStatus',
	assignmentType: 'assignmentType',
	taskStatus: 'taskStatus',
	assignmentStatus: 'assignmentStatus',
	taskType: 'taskType',
	timePhase: 'timePhase',
	milestone: 'milestone',
	auditPhase: 'auditPhase',
	findings: 'findings',
	documentConflict: 'documentConflict',
	downloadedForOffline: 'downloadedForOffline',
	entity: 'entity',
	tag: 'tag',
	schedulerRequest: 'schedulerRequest',
	schedulerOffering: 'schedulerOffering',
	documentSignedOffUsers: 'documentSignedOffUsers',
	epTasks: 'epTasks',
	eqrTasks: 'eqrTasks',
	filterValidationGroup: 'filterValidationGroup'
};

export const RnAFilterEntityTypes = {
	taskStatus: 'taskStatus',
	assignmentStatus: 'assignmentStatus'
};

export const TaskFilterEntityConfiguration = {
	engagementUser: {
		endpoint: 'users',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			userStatus: [userStatus.active, userStatus.pending, userStatus.deActivated, userStatus.scheduledInactive]
		}),
		entityMapping: (response) => {
			let users = response.users.map((item) => ({
				value: item.userId,
				label: item.engagementUserFirstName + ' ' + item.engagementUserLastName
			}));
			users.sort(function(a, b) {
				return a.label.localeCompare(b.label);
			});
			return users;
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value = '';

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			} else if (_filterData && _filterData.store && _filterData.store.notSignedOffBy) {
				value = _filterData.store.notSignedOffBy.byId[id] && _filterData.store.notSignedOffBy.byId[id].label;
			} else if (_filterData && _filterData.store && _filterData.store.signedOffBy) {
				value = _filterData.store.signedOffBy.byId[id] && _filterData.store.signedOffBy.byId[id].label;
			}

			return value;
		}
	},
	engagementUserRole: {
		endpoint: 'users',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			userStatus: [userStatus.active, userStatus.pending, userStatus.deActivated, userStatus.scheduledInactive]
		}),
		entityMapping: (response, engagementRoles) => {
			let users = response.users.map((item) => ({
				value: item.userId,
				label: item.engagementUserFirstName + ' ' + item.engagementUserLastName,
				group: labels.usersLabel
			}));
			users.sort(function(a, b) {
				return a.label.localeCompare(b.label);
			});

			each(engagementRoles || currentResource.RoleForMember, function(role) {
				users.push({
					value: '#' + role.id.toString(),
					label: role.name || role.role,
					group: labels.roles
				});
			});

			return users;
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId && _filterData.byId[id]) {
				if (Number.isInteger(id)) {
					// check id role is integer as navigating from dashboard because key's contain # with roleid
					id = '#' + id;
				}
				value = _filterData.byId[id].label;
			}

			if (!value && _filterData?.loggedInUserId && _filterData.loggedInUserId == id) {
				value = _filterData?.loggedInUserName;
			}

			return value;
		}
	},
	reviewNoteStatus: {
		data: 'filterReviewNoteStatus'
	},
	assignmentType: {
		data: 'filterAssignmentType'
	},
	taskStatus: {
		data: 'filterTaskStatus'
	},
	assignmentStatus: {
		data: 'filterAssignmentStatus'
	},
	taskType: {
		data: 'filterTaskTypeGroupEngagement'
	},
	documentSignedOffUsers: {
		endpoint: 'InternalUsers',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			userStatus: [userStatus.active, userStatus.pending, userStatus.deActivated, userStatus.scheduledInactive],
			IncludePagination: false,
			PageSize: -1
		}),
		entityMapping: (response) => {
			let users = response.users.map((item) => ({
				value: item.userId,
				label: item.firstName + ' ' + item.lastName
			}));
			users.sort(function(a, b) {
				return a.label.localeCompare(b.label);
			});

			return users;
		}
	},
	epTasks: {
		data: 'epTasksObj'
	},
	eqrTasks: {
		data: 'eqrTasksObj'
	},
	timePhase: {
		endpoint: 'timephases',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId
		}),
		entityMapping: (response) => {
			let timephases = [];
			response?.forEach((timephase) => {
				timephase.timePhases.length > 0 &&
					timephase.timePhases.forEach((timephaseitem) => {
						timephases.push({
							value: timephaseitem.id.toString(),
							label: timephaseitem.periodName
						});
					});
			});

			let orderedTimephases = orderBy(timephases, ['name'], ['asc']);
			orderedTimephases.push({
				value: '0',
				label: labels.noTimePhase
			});

			return orderedTimephases;
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId && _filterData.byId[id]) {
				value = _filterData.byId[id].label;
			}

			return value;
		}
	},
	milestone: {
		data: 'filterMilestones'
	},
	auditPhase: {
		data: 'filterAuditPhases'
	},
	documentConflict: {
		data: 'filterDocumentConflicts'
	},
	downloadedForOffline: {
		data: 'filterDownloadedForOffline'
	},
	findings: {
		data: 'filterTaskFindings'
	},
	entity: {
		endpoint: (entityFilters) => 'MultiEntity?pageSize' + entityFilters.pageSize,
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			pageSize: -1
		}),
		entityMapping: (response) =>
			response?.data?.map((item) => ({
				value: item.id,
				label: `${item?.stEntityShortName || ''} ${item?.stEntityName}`.trim()
			}))
	},
	tag: {
		endpoint: 'tags',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			entityId: 28
		}),
		entityMapping: (response) => {
			let tags = response?.data
				?.filter((x) => x.engagementTagId)
				.sort(function(a, b) {
					return (
						a.engagementTagGroupName.localeCompare(b.engagementTagGroupName) ||
						a.engagementTagName.localeCompare(b.engagementTagName)
					);
				})
				.map((item) => ({
					value: '#' + item.engagementTagId.toString(),
					label: item.engagementTagName,
					group: item.engagementTagGroupName
				}));

			return tags;
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId && _filterData.byId[id]) {
				value = _filterData.byId[id].label + '|' + _filterData.byId[id].group;
			}

			return value;
		}
	},
	schedulerRequest: {
		endpoint: 'schedulerRequests',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			includePagination: false
		}),
		entityMapping: (response) => {
			let schedulerRequests = [];
			response?.data?.forEach((sr) => {
				schedulerRequests.push({
					value: sr.id.toString(),
					label: sr.requestName
				});
			});

			schedulerRequests.sort(function(a, b) {
				return a.label.localeCompare(b.label);
			});

			return schedulerRequests;
		}
	},
	schedulerOffering: {
		endpoint: 'schedulerOfferings',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId
		}),
		entityMapping: (response) => {
			let schedulerOfferings = [];
			response?.forEach((sr) => {
				schedulerOfferings.push({
					value: sr.id.toString(),
					label: sr.name
				});
			});

			schedulerOfferings.sort(function(a, b) {
				return a.label.localeCompare(b.label);
			});

			return schedulerOfferings;
		}
	},
	filterValidationGroup: {
		data: 'missingEntityDocuments'
	}
};

export const GAFilterEntityTypes = {
	engagementUser: 'engagementUser',
	gaScopes: 'gaScopes',
	teamName: 'teamName',
	groupStructure: 'groupStructure'
};

export const GAFilterEntityConfiguration = {
	engagementUser: {
		endpoint: 'users',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			userStatus: [userStatus.active, userStatus.deActivated]
		}),
		entityMapping: (response) => {
			let users = response.users;

			users = orderBy(users, (user) => user.engagementUserFirstName + ' ' + user.engagementUserLastName);

			return users.map((item) => ({
				value: item.userId,
				label: item.engagementUserFirstName + ' ' + item.engagementUserLastName
			}));
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value = '';

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			} else if (_filterData && _filterData.store && _filterData.store.filterNotSignedOffByUser) {
				value =
					_filterData.store.filterNotSignedOffByUser.byId[id] &&
					_filterData.store.filterNotSignedOffByUser.byId[id].label;
			} else if (_filterData && _filterData.store && _filterData.store.filterSignedOffByUser) {
				value =
					_filterData.store.filterSignedOffByUser.byId[id] && _filterData.store.filterSignedOffByUser.byId[id].label;
			}

			return value;
		}
	},
	gaScopes: {
		endpoint: (entityFilters) => 'GroupTasks/' + entityFilters.taskId + '/GroupAuditScope',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			taskId: entityFilters.taskId
		}),
		entityMapping: (response) => {
			let scopes = response.groupScopes;

			scopes = orderBy(scopes, (scope) => scope.gaScopeName);

			return scopes.map((item) => ({value: item.gaScopeID, label: item.gaScopeName}));
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value = '';

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			} else if (_filterData && _filterData.store && _filterData.store.FilterGroupScope) {
				value = _filterData.store.FilterGroupScope.byId[id] && _filterData.store.FilterGroupScope.byId[id].label;
			}

			return value;
		}
	},
	teamName: {
		endpoint: (entityFilters) => 'GroupTasks/' + entityFilters.taskId + '/GroupComponents',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			taskId: entityFilters.taskId
		}),
		entityMapping: (response) => {
			let groupTaskComponents = response.groupTaskComponents;
			groupTaskComponents = orderBy(groupTaskComponents, (groupTaskComponent) =>
				groupTaskComponent.gaScopeName.toLowerCase()
			);
			groupTaskComponents = orderBy(groupTaskComponents, (groupTaskComponent) =>
				groupTaskComponent.gaComponentName.toLowerCase()
			);

			return groupTaskComponents.map((item) => ({
				value: item.gaComponentID,
				label: item.gaComponentName,
				group: item.gaScopeName
			}));
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value = '';

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			} else if (_filterData && _filterData.store && _filterData.store.FilterGroupAuditComponent) {
				value =
					_filterData.store.FilterGroupAuditComponent.byId[id] &&
					_filterData.store.FilterGroupAuditComponent.byId[id].label;
			}

			return value;
		}
	},
	groupStructure: {
		getDisplayValue: (id, entityType, _filterData) => {
			if (id === GARoleTypeId.Region) return currentResource.gaDocFilters.region;
			else return currentResource.gaDocFilters.component;
		}
	}
};

export const CanvasFormUpdateFilterEntityTypes = {
	canvasFormType: 'canvasFormType',
	sourceOfForm: 'sourceOfForm'
};

export const CanvasFormUpdateFilterEntityConfiguration = {
	sourceOfForm: {
		data: 'filterSourceOfForm'
	},
	canvasFormType: {
		endpoint: 'documenttypes',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			parentDocumentTypes: [documentTypes.SMARTFORMDOCUMENT, documentTypes.CANVASFORM],
			excludeDocumentTypes: [documentTypes.PROFILEV2]
		}),
		entityMapping: (response) => {
			let resp = [];
			response?.forEach((sr) => {
				if (sr.documentTypeName && sr.documentTypeName.length > 0) {
					resp.push({
						value: sr.documentTypeName,
						label: sr.documentTypeName
					});
				}
			});

			resp.sort(function(a, b) {
				return a.label.localeCompare(b.label);
			});

			return resp;
		}
	}
};

export const RNHFilterEntityTypes = {
	engagementUser: 'engagementUser',
	priorityType: 'priorityType',
	dueDateEnd: 'dueDateEnd',
	dueDateStart: 'dueDateStart'
};

export const RNHFilterEntityConfiguration = {
	engagementUser: {
		endpoint: 'users',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			userStatus: [userStatus.active, userStatus.pending, userStatus.deActivated, userStatus.scheduledInactive]
		}),
		entityMapping: (response) => {
			let users = response.users;

			users = orderBy(users, (user) => user.engagementUserFirstName + ' ' + user.engagementUserLastName);

			return users.map((item) => ({
				value: item.userId,
				label: item.engagementUserFirstName + ' ' + item.engagementUserLastName
			}));
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id]?.label;
			}

			return value;
		}
	},
	priorityType: {
		getDisplayValue: (id, entityType, _filterData) => currentResource.priorityTypes.find((x) => x.value == id)?.label
	},
	reviewNoteStatus: {
		getDisplayValue: (id, entityType, _filterData) => currentResource.reviewNoteStatus.find((x) => x.value == id)?.label
	}
};

export const RnATasksFilterConfiguration = {
	taskStatus: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.quickFilterTaskStatus.find((x) => x.value == id)?.label
	},
	assignmentStatus: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.quickFilterAssignmentStatus.find((x) => x.value == id)?.label
	}
};

export const ActivityFeedDocFilterEntityTypes = {
	engagementUser: 'engagementUser',
	reviewNoteStatus: 'reviewNoteStatus',
	fileExtension: 'fileExtension',
	documentContent: 'documentContent',
	sharedEvidence: 'sharedEvidence',
	groupAuditComponent: 'groupAuditComponent',
	documentFindings: 'documentFindings',
	canvasForms: 'canvasForms',
	groupInstruction: 'groupInstruction',
	signoffRequirements: 'signoffRequirements',
	missingRelatedTask: 'missingRelatedTask',
	responsiveToRisk: 'responsiveToRisk'
};

export const ActivityFeedDocFilterEntityConfiguration = {
	engagementUser: {
		endpoint: 'users',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			userStatus: [userStatus.active, userStatus.deActivated]
		}),
		entityMapping: (response) => {
			let users = response.users;

			users = orderBy(users, (user) => user.engagementUserFirstName + ' ' + user.engagementUserLastName);

			return users.map((item) => ({
				value: item.userId,
				label: item.engagementUserFirstName + ' ' + item.engagementUserLastName
			}));
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			}

			return value;
		}
	},
	reviewNoteStatus: {
		data: 'filterDocListReviewNoteStatus'
	},
	fileExtension: {
		data: 'filterFileExtension'
	},
	documentContent: {
		data: 'filterDocumentContent'
	},
	sharedEvidence: {
		data: 'filterSharedEvidence'
	},
	signoffRequirements: {
		data: 'filterSignoffRequirements'
	},
	responsiveToRisk: {
		data: 'filterResponsiveToRisks'
	},
	groupAuditComponent: {
		endpoint: 'groupcomponents',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId
		}),
		entityMapping: (response) =>
			response.data?.map((item) => ({
				value: item.id,
				label: item.shortName
			})),
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id]?.label;
			}

			return value;
		}
	},
	documentFindings: {
		data: 'filterFindings'
	},
	canvasForms: {
		data: 'filterCanvasForms'
	},
	groupInstruction: {
		endpoint: 'tasks',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			categoryId: TaskCategory.all,
			subCategoryId: TaskSubCategory.all,
			taskType: TaskTypeEnum.GROUP_TASK
		}),
		entityMapping: (response) => {
			if (isEmpty(response) && isEmpty(response.taskGroups)) {
				return [];
			}

			const instructions = response.taskGroups[0]?.tasks.map((item) => ({
				value: item.id,
				label: item.name
			}));

			return orderBy(instructions, (inst) => inst.label);
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id]?.label;
			}

			return value;
		}
	}
};

export const FilterTypeConfiguration = {
	[FilterTypes.search]: {
		getDisplayValue: (id, entityType, filterData) => {
			let value;
			if (currentResource[FilterEntityConfiguration[entityType].data]) {
				value = currentResource[FilterEntityConfiguration[entityType].data].find(
					(item) => item.value.toString().toLowerCase() === id.toString().toLowerCase()
				)?.label;
			} else {
				value = filterData?.byId && filterData?.byId[id]?.label;
			}
			return value;
		}
	},
	[FilterTypes.boolean]: {
		getDisplayValue: (value) => (value ? labels.filterYes : labels.filterNo)
	},
	[FilterTypes.string]: {
		getDisplayValue: (id, entityType, props) => {
			return props.showCustomLabel ? props.customLabel : props.label;
		}
	}
};

export const ControlsFilterEntityTypes = {
	scots: 'scots',
	testing: 'testing',
	designEffectiveness: 'designEffectiveness'
};

export const ControlsFilterTypeConfiguration = {
	[FilterTypes.search]: {
		getDisplayValue: (id, entityType, filterData) => {
			return filterData?.data.byId && filterData.data.byId[id]?.label;
		}
	}
};

export const TaskFilterConfiguration = {
	filterGroupTasks: {
		label: labels.filterGroupTasksLabel,
		type: FilterTypes.menu,
		menu: {
			tasktype: {
				label: labels.filterTaskTypeLabel,
				type: FilterTypes.search,
				entityType: TaskFilterEntityTypes.taskType,
				searchPlaceholder: labels.placeholderForSearch,
				noResultsPlaceholder: labels.noResultsAvavilable,
				filterOrder: 1,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			eptasks: {
				label: labels.epTasks,
				type: FilterTypes.search,
				entityType: TaskFilterEntityTypes.epTasks,
				searchPlaceholder: labels.placeholderForSearch,
				noResultsPlaceholder: labels.noResultsAvavilable,
				filterOrder: 17,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			eqrtasks: {
				label: labels.eqrTasks,
				type: FilterTypes.search,
				entityType: TaskFilterEntityTypes.eqrTasks,
				searchPlaceholder: labels.placeholderForSearch,
				noResultsPlaceholder: labels.noResultsAvavilable,
				filterOrder: 18,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			}
		}
	},
	filterGroupAssignments: {
		label: labels.filterGroupAssignmentsLabel,
		type: FilterTypes.menu,
		menu: {
			assignedto: {
				label: labels.filterAssignedToLabel,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				type: FilterTypes.search,
				entityType: TaskFilterEntityTypes.engagementUserRole,
				dataMapping: (response) => {
					return TaskFilterEntityConfiguration.engagementUserRole.entityMapping(response, props.engagementRoles);
				},
				dataGroupBy: (item) => item.group,
				filterOrder: 2,
				getDisplayValue: TaskFilterEntityConfiguration.engagementUserRole.getDisplayValue
			},
			assignmenttype: {
				label: labels.filterAssignmentTypeLabel,
				type: FilterTypes.search,
				entityType: TaskFilterEntityTypes.assignmentType,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				data: currentResource.filterAssignmentType,
				filterOrder: 3,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			}
		}
	},
	filterGroupReviewNotes: {
		label: labels.filterGroupReviewNotesLabel,
		type: FilterTypes.menu,
		menu: {
			reviewnotesstatus: {
				label: labels.filterTaskReviewNoteStatusLabel,
				type: FilterTypes.search,
				entityType: TaskFilterEntityTypes.reviewNoteStatus,
				data: currentResource.filterReviewNoteStatus,
				searchPlaceholder: labels.placeholderForSearch,
				noResultsPlaceholder: labels.noResultsAvavilable,
				filterOrder: 4,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			reviewnotesassignedto: {
				label: labels.filterTaskReviewNoteAssignedToLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.engagementUser,
				dataMapping: TaskFilterEntityConfiguration.engagementUser.entityMapping,
				filterOrder: 5,
				getDisplayValue: TaskFilterEntityConfiguration.engagementUser.getDisplayValue
			},
			reviewnotesauthoredby: {
				label: labels.filterTaskReviewNoteAuthoredByLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.engagementUser,
				dataMapping: TaskFilterEntityConfiguration.engagementUser.entityMapping,
				filterOrder: 6,
				getDisplayValue: TaskFilterEntityConfiguration.engagementUser.getDisplayValue
			}
		}
	},
	filterGroupDocumentsignoffs: {
		label: labels.filterGroupDocumentsignoffsLabel,
		type: FilterTypes.menu,
		menu: {
			missingSignOffs: {
				label: labels.filterSignOffMissingLabel,
				type: FilterTypes.boolean,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.boolean].getDisplayValue
			},
			nodocumentsignedoffby: {
				label: labels.NoDocumentSignedOffLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.documentSignedOffUsers,
				dataMapping: TaskFilterEntityConfiguration.documentSignedOffUsers.entityMapping,
				filterOrder: 11,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			somedocumentsignedoffby: {
				label: labels.SomeDocumentSignedOffLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.documentSignedOffUsers,
				dataMapping: TaskFilterEntityConfiguration.documentSignedOffUsers.entityMapping,
				filterOrder: 12,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			alldocumentsignedoffby: {
				label: labels.AllDocumentSignedOffLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.documentSignedOffUsers,
				dataMapping: TaskFilterEntityConfiguration.documentSignedOffUsers.entityMapping,
				filterOrder: 13,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			}
		}
	},
	filterGroupProjectManagement: {
		label: labels.filterGroupProjectManagementLabel,
		type: FilterTypes.menu,
		menu: {
			timephase: {
				label: labels.filterTimePhaseLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.timePhase,
				dataMapping: TaskFilterEntityConfiguration.timePhase.entityMapping,
				filterOrder: 7,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			milestone: {
				label: labels.filterMilestoneLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.milestone,
				data: currentResource.filterMilestones,
				filterOrder: 8,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			auditphase: {
				label: labels.filterEngagementPhaseLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.auditPhase,
				data: currentResource.filterAuditPhases,
				filterOrder: 9,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			tag: {
				label: labels.tagsLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				search: true,
				dataMapping: TaskFilterEntityConfiguration.tag.entityMapping,
				dataGroupBy: (item) => item.group,
				entityType: TaskFilterEntityTypes.tag,
				filterOrder: 10,
				getDisplayValue: TaskFilterEntityConfiguration.tag.getDisplayValue
			},
			nextwaveServices: {
				label: labels.filterServiceGateway,
				type: FilterTypes.menu,
				menu: {
					schedulerrequest: {
						label: labels.filterScheduledServicesLabel,
						type: FilterTypes.search,
						noResultsPlaceholder: labels.noResultsAvavilable,
						searchPlaceholder: labels.placeholderForSearch,
						entityType: TaskFilterEntityTypes.schedulerRequest,
						dataMapping: TaskFilterEntityConfiguration.schedulerRequest.entityMapping,
						filterOrder: 11,
						getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
					},
					scheduleroffering: {
						label: labels.filterSuggestedServicesLabel,
						type: FilterTypes.search,
						noResultsPlaceholder: labels.noResultsAvavilable,
						searchPlaceholder: labels.placeholderForSearch,
						entityType: TaskFilterEntityTypes.schedulerOffering,
						dataMapping: TaskFilterEntityConfiguration.schedulerOffering.entityMapping,
						filterOrder: 12,
						getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
					}
				}
			}
		}
	},
	filterGroupOther: {
		label: labels.filterGroupOtherLabel,
		type: FilterTypes.menu,
		menu: {
			documentconflict: {
				label: labels.filterDocumentConflictsLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.documentConflict,
				data: currentResource.filterDocumentConflicts,
				filterOrder: 13,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			findings: {
				label: labels.filterFindingsLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.findings,
				data: currentResource.filterTaskFindings,
				//visible: props.engagement.isAuditEngagement === true,
				filterOrder: 14,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			isbookmarked: {
				label: labels.filterDownloadedForOfflineLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				entityType: TaskFilterEntityTypes.downloadedForOffline,
				data: currentResource.filterDownloadedForOffline,
				filterOrder: 15,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			},
			entity: {
				label: labels.filterEntitiesLabel,
				type: FilterTypes.search,
				noResultsPlaceholder: labels.noResultsAvavilable,
				searchPlaceholder: labels.placeholderForSearch,
				//visible: props.engagement.isMestEngagement === true,
				entityType: TaskFilterEntityTypes.entity,
				dataMapping: TaskFilterEntityConfiguration.entity.entityMapping,
				filterOrder: 16,
				getDisplayValue: FilterTypeConfiguration[FilterTypes.search].getDisplayValue
			}
		}
	}
};

export const ClientRequestEntityTypes = {
	assignedTo: 'assignedTo',
	attachements: 'attachements',
	timePhases: 'timePhases',
	filtercreatedby: 'filtercreatedby',
	filterStatus: 'filterStatus',
	filterErrors: 'filterErrors',
	filterMilestones: 'filterMilestones',
	filterOverdue: 'filterOverdue',
	clientRequestsTags: 'clientRequestsTags',
	filtertype: 'filtertype',
	filterPriority: 'filterPriority'
};

export const ClientRequestsFilterConfiguration = {
	assignedTo: {
		entityMapping: (response, includeUserType = false) => {
			const usersList = _.orderBy(
				response.externalUsers,
				[[(user) => user.firstName.toLowerCase()], [(user) => user.lastName.toLowerCase()]],
				['asc', 'asc']
			);
			const clientGroupsList = _.orderBy(response.clientGroups, [(group) => group.groupName.toLowerCase()], ['asc']);

			const uniqueUsers = [];
			const map = new Map();
			for (const user of usersList) {
				if (!map.has(user.userId)) {
					map.set(user.userId, true); // set any value to Map
					const userItem = {
						value: user.userId,
						label: `${user.firstName} ${user.lastName}`
					};
					// needed user grouping on Collab Request Bulk modal
					if (includeUserType) {
						userItem.userType = labels.externalUsersOption;
					}
					uniqueUsers.push(userItem);
				}
			}

			return [
				...uniqueUsers,
				...clientGroupsList.map((x) => {
					const groupItem = {
						value: x.groupId,
						label: x.groupName
					};
					// needed user grouping on Collab Request Bulk modal
					if (includeUserType) {
						groupItem.userType = labels.groupsOption;
					}
					return groupItem;
				})
			];
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			}

			if (_filterData?.store && _filterData?.store[entityType]?.byId) {
				value = _filterData?.store[entityType]?.byId[id] && _filterData?.store[entityType]?.byId[id].label;
			}

			return value;
		},
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId
		})
	},
	attachements: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.clientRequestsAttachmentsFilter.find((x) => x.value == id)?.label
	},
	timePhases: {
		endpoint: 'TimePhases',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId
		}),
		entityMapping: (response) => {
			let timephases = [];
			response.forEach((x) => {
				timephases.push(...x.timePhases);
			});
			return timephases.map((item) => ({
				value: item.id,
				label: item.periodName
			}));
			// return [];
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			}

			if (_filterData?.store && _filterData?.store[entityType]?.byId) {
				value = _filterData?.store[entityType]?.byId[id] && _filterData?.store[entityType]?.byId[id].label;
			}
			return value;
		}
	},
	clientRequestsTags: {
		endpoint: 'tags',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId,
			entityId: ['17'],
			syncStatusIds: ['2', '4', '9']
		}),
		entityMapping: (response) => {
			return response.data.map((x) => {
				return {value: x.engagementTagId, label: x.engagementTagName};
			});

			// return []
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			}

			if (_filterData?.store && _filterData?.store[entityType]?.byId) {
				value = _filterData?.store[entityType]?.byId[id] && _filterData?.store[entityType]?.byId[id].label;
			}

			return value;
		}
	},
	filtercreatedby: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.clientRequestsCreatedByFilter.find((x) => x.value == id)?.label
	},
	filterStatus: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.clientRequestsStatusFilter.find((x) => x.value == id)?.label
	},
	filterErrors: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.clientRequestsBooleanFilters.find((x) => x.value == id)?.label
	},
	filterOverdue: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.clientRequestsBooleanFilters.find((x) => x.value == id)?.label
	},
	filtertype: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.clientRequestsTypeFilter.find((x) => x.value == id)?.label
	},
	filterPriority: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.clientRequestsPriorityFilter.find((x) => x.value == id)?.label
	},
	hasAnyRelatedTask: {
		getDisplayValue: (id, entityType, _filterData) =>
			currentResource.bulkClientRequestsNoRelatedTaskFilter.find((x) => x.value == id)?.label
	}
};

export const ScotFilterConfiguration = {
	scots: {
		endpoint: 'scots',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId
		}),
		entityMapping: (response) => {
			return response?.data?.map((item) => {
				return {value: item.id, label: item.scotName};
			});
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			}

			if (_filterData?.store && _filterData?.store[entityType]?.byId) {
				value = _filterData?.store[entityType]?.byId[id] && _filterData?.store[entityType]?.byId[id].label;
			}
			return value;
		}
	}
};

export const ITProcessFilterConfiguration = {
	itprocess: {
		endpoint: 'itprocess',
		entityFilter: (entityFilters) => ({
			engagementId: entityFilters.engagementId
		}),
		entityMapping: (response) => {
			return response?.data?.map((item) => {
				return {value: item.id, label: item.itProcessName};
			});
		},
		getDisplayValue: (id, entityType, _filterData) => {
			let value;

			if (_filterData && _filterData.byId) {
				value = _filterData.byId[id] && _filterData.byId[id].label;
			}

			if (_filterData?.store && _filterData?.store[entityType]?.byId) {
				value = _filterData?.store[entityType]?.byId[id] && _filterData?.store[entityType]?.byId[id].label;
			}
			return value;
		}
	}
};

export const DocumentListFilterConfigurationKeys = [
	'filterGroupDocuments',
	'filterDocumentContent',
	'filterSharedEvidence',
	'filterFileExtension',
	'filterDocumentConflict',
	'filterDuplicateFileName',
	'filterCanvasFormContent',
	'filterGroupModifications',
	'filterLastModifyUser',
	'filterModifyAfterReviwerSignoff',
	'filterModifiedAfter',
	'filterGroupReviewNotes',
	'filterReviewNoteStatus',
	'filterReviewNoteAssignedTo',
	'filterReviewNoteAuthoredBy',
	'filterReviewNoteOtherEngagement',
	'filterGroupSignOffs',
	'filterSignOffMissing',
	'filterSignedOffByUser',
	'filterNotSignedOffByUser',
	'filterSignoffRequirements',
	'filterGroupOther',
	'filterGroupAuditComponent',
	'filterTaskId',
	'filterFindings',
	'searchString',
	'searchType',
	'filterTimePhase',
	'filterDocumentId'
];

export const FilterEntityTypes = {
	...DocumentFilterEntityTypes,
	...TaskFilterEntityTypes,
	...GAFilterEntityTypes,
	...CanvasFormUpdateFilterEntityTypes,
	...RNHFilterEntityTypes,
	...ActivityFeedDocFilterEntityTypes,
	...ClientRequestEntityTypes
};

export const FilterEntityConfiguration = {
	...DocumentFilterEntityConfiguration,
	...TaskFilterEntityConfiguration,
	...GAFilterEntityConfiguration,
	...CanvasFormUpdateFilterEntityConfiguration,
	...RNHFilterEntityConfiguration,
	...ActivityFeedDocFilterEntityConfiguration,
	...ClientRequestsFilterConfiguration,
	...ScotFilterConfiguration,
	...ITProcessFilterConfiguration
};

export function isSyncQueryStringRequired(location) {
	const syncQuerystring = location.search.includes(`category=${ViewTypes.documents}`);
	return syncQuerystring;
}
